/**
 * Wallet sign-in: a nonce from the Maker portal's API → an EIP-4361 message
 * → `personal_sign` by the connected account → `POST /maker-api/auth/login`.
 * The message names the wallet's network when the API serves it, else the
 * first one served: signing costs no gas and needs no switch.
 */

import { getAddress } from "viem";
import { createSiweMessage } from "viem/siwe";
import { api, ApiError } from "./api";
import { saveSession, type MakerSession } from "./session";
import * as wallet from "./wallet";

export type SignInResult = { kind: "signed-in"; session: MakerSession } | { kind: "not-an-operator" };

/** The chain the message names: the wallet's own when served, else the first served. */
async function messageChain(chainIds: readonly number[]): Promise<number> {
  const first = chainIds[0];
  if (first === undefined) throw new ApiError("The sign-in nonce named no network", "/maker-api/auth/nonce");
  try {
    const current = await wallet.getChainId();
    return chainIds.includes(current) ? current : first;
  } catch {
    return first;
  }
}

/** The exact text the wallet shows and signs. */
export function buildSignInMessage(n: { domain: string; uri: string; statement: string; nonce: string; issuedAt: string; expiresAt: string }, address: string, chainId: number): string {
  return createSiweMessage({
    domain: n.domain,
    address: getAddress(address),
    statement: n.statement,
    uri: n.uri,
    version: "1",
    chainId,
    nonce: n.nonce,
    issuedAt: new Date(n.issuedAt),
    expirationTime: new Date(n.expiresAt),
  });
}

/**
 * Signs in `address` (the connected account). Throws `WalletError` for a
 * wallet failure (4001 = refused, never reported) and `ApiError` for a refused
 * or failed call; a valid signature from a wallet that is no operator resolves
 * `not-an-operator` and keeps no session.
 */
export async function signInWithWallet(address: string): Promise<SignInResult> {
  const nonce = await api.nonce();
  const chainId = await messageChain(nonce.chainIds);
  const message = buildSignInMessage(nonce, address, chainId);
  const signature = await wallet.signMessage(address, message);
  try {
    const res = await api.login({ message, signature });
    const session: MakerSession = { token: res.token, kind: "wallet", operator: res.operator, company: res.company, expiresAt: res.expiresAt };
    saveSession(session);
    return { kind: "signed-in", session };
  } catch (err) {
    if (err instanceof ApiError && err.status === 403) return { kind: "not-an-operator" };
    throw err;
  }
}
